import { useMemo } from 'react';

import useLocalStorage from '@/hooks/useLocalStorage';
import {
  type DateSelection,
  DEFAULT_DATE_SELECTION,
  normalizeSelection,
  resolveDateSelection,
} from '@/modules/analytics/dateSelection';

/**
 * Persists a date-filter *selection* (intent) and derives the concrete range from today on every
 * render. Relative presets therefore roll forward automatically instead of freezing at the day
 * they were picked. Pair with `LocalDateFilter`.
 *
 * @param defaultSelection Pass a stable reference (module-level constant), not an inline object —
 * it's a `useMemo` dependency, so a fresh object each render would recompute the range and refire
 * dependent queries on every render.
 */
export const usePersistedDateRange = (
  storageKey: string,
  defaultSelection: DateSelection = DEFAULT_DATE_SELECTION
) => {
  const [stored, setSelection] = useLocalStorage<DateSelection>(storageKey, defaultSelection);

  const selection = useMemo(
    () => normalizeSelection(stored, defaultSelection),
    [stored, defaultSelection]
  );
  const dateRange = useMemo(() => resolveDateSelection(selection), [selection]);

  return { selection, setSelection, dateRange };
};
